
import { Procurement } from '@/types/database.types';

/**
 * Get a summary of products in the procurement
 */
export const getProductsSummary = (procurement: Procurement): string => {
  // If there are multiple items, show each product individually
  if (procurement.items && procurement.items.length > 0) {
    const productLines = procurement.items.map(item => {
      const quantity = item.quantity || 0;
      const pricePerUnit = item.price_per_unit || 0;
      const totalItemAmount = quantity * pricePerUnit;
      
      return `• ${item.product_name} (${quantity}kg @ Br${pricePerUnit.toFixed(2)}/kg = Br${totalItemAmount.toFixed(2)})`;
    });
    
    return `Products:\n${productLines.join('\n')}`;
  }
  
  // Fallback to single product display
  if (!procurement.product_name) {
    return 'Product information not available';
  }
  
  const quantity = procurement.total_quantity || 0;
  const amount = procurement.total_amount || 0;
  const unitPrice = quantity > 0 ? (amount / quantity).toFixed(2) : '0.00';
  
  // Use plain text formatting to avoid Markdown parsing issues
  return `${procurement.product_name} (${quantity}kg @ Br${unitPrice}/kg)`;
};

/**
 * Format requestor information
 */
export const formatRequestorInfo = (requestor?: any): string => {
  if (!requestor) return '';
  
  const name = requestor.first_name || requestor.last_name 
    ? `${requestor.first_name || ''} ${requestor.last_name || ''}`.trim()
    : 'Unknown';
  
  return `Requested by: ${name}`;
};

/**
 * Format approver information
 */
export const formatApproverInfo = (approver?: any): string => {
  if (!approver) return '';
  
  const name = approver.first_name || approver.last_name 
    ? `${approver.first_name || ''} ${approver.last_name || ''}`.trim()
    : 'Unknown';
  
  return `Approved by: ${name}`;
};

/**
 * Format rejector information
 */
export const formatRejectorInfo = (rejector?: any): string => {
  if (!rejector) return '';
  
  const name = rejector.first_name || rejector.last_name 
    ? `${rejector.first_name || ''} ${rejector.last_name || ''}`.trim()
    : 'Unknown';
  
  return `Rejected by: ${name}`;
};

/**
 * Format source information - simplified to work with base Procurement type
 */
export const formatSourceInfo = (procurement: Procurement): string => {
  // Use source_name if available (enriched from service layer)
  if (procurement.source_name) {
    return procurement.source_name;
  }
  
  // Fallback based on source_type
  switch (procurement.source_type) {
    case 'farm':
      return 'Farm Source';
    case 'supplier':
      return 'Supplier Source';
    case 'agent':
      return 'Agent Source';
    case 'harvest':
      return 'Harvest Source';
    default:
      return 'Unknown Source';
  }
};

/**
 * Format payment information
 */
export const formatPaymentInfo = (procurement: Procurement): string => {
  if (!procurement.finance) return '';
  
  const paymentType = procurement.finance.payment_term_type || 'upfront';
  const paymentDays = procurement.finance.payment_term_days || 0;
  
  if (paymentType === 'term' && paymentDays > 0) {
    return `Payment: Due in ${paymentDays} days (term payment)`;
  } else {
    return `Payment: Due immediately (upfront payment)`;
  }
};

/**
 * Format quality inspection information
 * Focus on quality findings only - no net payable calculations
 */
export const formatQualityInfo = (procurement: Procurement): string => {
  const extendedProcurement = procurement as any;
  const quality = extendedProcurement?.quality;
  
  if (!quality) {
    return '';
  }

  const isPreliminary = quality.grading_type === 'preliminary';
  const isFinalComplete = quality.is_final_grading_complete;
  
  let qualityLines: string[] = [];
  
  // Add grading type indicator
  if (isPreliminary) {
    qualityLines.push(`**Grading Type:** Sample-based (Preliminary)`);
    if (quality.final_grading_expected_date) {
      qualityLines.push(`**Expected Final Grading:** ${quality.final_grading_expected_date}`);
    }
  }
  
  // Show preliminary values if it's a preliminary assessment
  if (isPreliminary && (quality.preliminary_damage_kg > 0 || quality.preliminary_compensation_kg > 0)) {
    qualityLines.push(`**Estimated Damage:** ${quality.preliminary_damage_kg || 0} kg`);
    qualityLines.push(`**Estimated Compensation:** ${quality.preliminary_compensation_kg || 0} kg`);
  }
  
  // Show final values if final grading is complete
  if (isFinalComplete) {
    qualityLines.push(`**Actual Damage:** ${quality.damage_kg || 0} kg`);
    qualityLines.push(`**Actual Compensation:** ${quality.compensation_kg || 0} kg`);
    
    // Show difference if there was a preliminary assessment
    if (isPreliminary) {
      const prelimComp = quality.preliminary_compensation_kg || 0;
      const finalComp = quality.compensation_kg || 0;
      const diff = finalComp - prelimComp;
      if (diff !== 0) {
        qualityLines.push(`**Difference:** ${diff > 0 ? '+' : ''}${diff} kg (${diff > 0 ? 'more damage than estimated' : 'less damage than estimated'})`);
      }
    }
  }
  
  // For non-preliminary final grading, show standard quality info
  if (!isPreliminary && (quality.damage_kg > 0 || quality.compensation_kg > 0)) {
    qualityLines.push(`**Damage Found:** ${quality.damage_kg || 0} kg`);
    qualityLines.push(`**Agreed Compensation:** ${quality.compensation_kg || 0} kg`);
  }
  
  // NO net payable calculations - quality focuses on findings only
  
  if (qualityLines.length === 0) {
    return '';
  }
  
  return '\n' + qualityLines.join('\n');
};

// formatBankInfo is no longer exported - bank info should not be in Telegram notifications
